import { t } from "@lingui/macro";
import {
  Article,
  Browser,
  ChartBar,
  CircleNotch,
  FilePdf,
  Info,
  List as ListIcon,
  PaintBrushHousehold,
  Palette,
  TextAa,
  X as XIcon,
} from "@phosphor-icons/react";
import { useBreakpoint, useTouchOptimization } from "@stylingcv/hooks";
import {
  Button,
  Panel,
  PanelGroup,
  PanelResizeHandle,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Tooltip,
  VisuallyHidden,
} from "@stylingcv/ui";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { cn } from "@stylingcv/utils";
import { useCssAnimation } from "../../hooks/use-css-animation";
import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";

import { StylionAtmosphere } from "@/client/components/stylion-atmosphere";
import { SignupModal } from "@/client/components/auth/signup-modal";
import { PdfLoadingModal } from "@/client/components/pdf-loading-modal";
import { useTemplateAccess } from "@/client/hooks/use-template-access";
import { useToast } from "@/client/hooks/use-toast";
import { Sidebar } from "@/client/pages/dashboard/_components/sidebar";
import { usePrintResume } from "@/client/services/resume";
import { useAuthStore } from "@/client/stores/auth";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";
import { forceDownload } from "@/client/utils/download";
import { trackDownloadResume } from "@/client/utils/gtm";

import { useResumeLimits } from "@/client/hooks/use-resume-limits";
import { useResumes } from "@/client/services/resume";

import { BuilderLoadingOverlay } from "./_components/builder-loading-overlay";
import { BuilderSidebar } from "./_components/builder-sidebar";
import { BuilderTabs } from "./_components/builder-tabs";
import type { BuilderTab, DocumentType } from "./_components/builder-tabs";
import { BuilderTopbar } from "./_components/builder-topbar";
import { CoverLetterGenerationModal } from "./_components/cover-letter-generation-modal";
import { DocumentLimitModal } from "./_components/document-limit-modal";
import { TabletBuilderHeader } from "./_components/header";
import { BuilderToolbar } from "./_components/toolbar";
import { LeftNavigationRail } from "./_components/rails/left-navigation-rail";
import { SectionRail } from "./_components/rails/section-rail";
import { LeftSidebar } from "./sidebars/left";
import { RightSidebar } from "./sidebars/right";
import { MobileLayout } from "./layouts/mobile-layout";

const onOpenAutoFocus = (event: Event) => {
  event.preventDefault();
};

// Floating Download Button Component with CSS Animation
interface FloatingDownloadButtonProps {
  isVisible: boolean;
  loading: boolean;
  onClick: () => void;
}

const FloatingDownloadButton = ({ isVisible, loading, onClick }: FloatingDownloadButtonProps) => {
  const { shouldRender, getAnimationClasses } = useCssAnimation(isVisible, {
    duration: 300,
    exitDuration: 300,
  });

  if (!shouldRender) return null;

  return (
    <div
      className={cn(
        "fixed bottom-24 right-4 z-[60] transition-all duration-300",
        getAnimationClasses(
          "opacity-100 translate-y-0 scale-100",
          "opacity-0 translate-y-5 scale-90"
        )
      )}
    >
      <Button
        size="sm"
        className="h-10 rounded-full bg-primary px-4 text-xs font-bold shadow-2xl shadow-primary/40 ring-2 ring-background transition-transform active:scale-95"
        onClick={onClick}
      >
        {loading ? (
          <CircleNotch className="mr-2 rtl:ml-2 rtl:mr-0 size-5 animate-spin" />
        ) : (
          <FilePdf className="mr-2 rtl:ml-2 rtl:mr-0 size-5" />
        )}
        {loading ? t`Generating...` : t`Download PDF`}
      </Button>
    </div>
  );
};



// Mobile Preview Component
const MobilePreview = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [showPdfLoadingModal, setShowPdfLoadingModal] = useState<boolean>(false);

  // PDF Loading Steps State (same as main mobile layout)
  const [pdfSteps, setPdfSteps] = useState<
    {
      id: string;
      title: string;
      description: string;
      status: "pending" | "in-progress" | "completed" | "error";
      progress?: number;
    }[]
  >([
    {
      id: "1",
      title: t`Preparing Resume`,
      description: t`Gathering resume data...`,
      status: "pending",
    },
    {
      id: "2",
      title: t`Generating PDF`,
      description: t`Converting to PDF format...`,
      status: "pending",
      progress: 0,
    },
    {
      id: "3",
      title: t`Finalizing Export`,
      description: t`Preparing download...`,
      status: "pending",
    },
    {
      id: "4",
      title: t`Download Ready`,
      description: t`PDF generation complete!`,
      status: "pending",
    },
  ]);

  // PDF Steps Management
  const updatePdfStep = (stepId: string, updates: Partial<(typeof pdfSteps)[0]>) => {
    setPdfSteps((prev) =>
      prev.map((step) => (step.id === stepId ? { ...step, ...updates } : step)),
    );
  };

  // Auth and resume data
  const user = useAuthStore((state) => state.user);
  const isLoggedIn = !!user;
  const { id, title } = useResumeStore((state) => state.resume);
  const { printResume, loading } = usePrintResume();

  // Get basic user data for prefilling the signup form
  const userData = useResumeStore((state) => {
    try {
      return {
        name: state.resume.data.basics.name || "",
        email: state.resume.data.basics.email || "",
      };
    } catch {
      return {
        name: "",
        email: "",
      };
    }
  });

  // Template access check
  const templateTheme = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.theme;
    } catch {
      return {};
    }
  });
  const { canDownloadPdf } = useTemplateAccess(templateTheme);

  const handleDownloadPdf = async () => {
    const { resume } = useResumeStore.getState();
    const templateName = resume?.data?.metadata?.template;

    if (!isLoggedIn) {
      trackDownloadResume({
        resumeId: id,
        format: "pdf",
        templateName,
        outcome: "redirect_to_signup",
      });
      setShowAuthModal(true);
      return;
    }

    // Check template access permissions
    if (!canDownloadPdf) {
      trackDownloadResume({
        resumeId: id,
        format: "pdf",
        templateName,
        outcome: "redirect_to_pricing",
      });

      navigate("/pricing");
      return;
    }

    // Reset PDF steps and show loading modal with progress
    setPdfSteps([
      {
        id: "1",
        title: "Preparing Resume",
        description: "Gathering resume data...",
        status: "pending",
      },
      {
        id: "2",
        title: "Generating PDF",
        description: "Converting to PDF format...",
        status: "pending",
        progress: 0,
      },
      {
        id: "3",
        title: "Finalizing Export",
        description: "Preparing download...",
        status: "pending",
      },
      {
        id: "4",
        title: "Download Ready",
        description: "PDF generation complete!",
        status: "pending",
      },
    ]);
    setShowPdfLoadingModal(true);

    // Use setTimeout to ensure state update and re-render happens
    void setTimeout(async () => {
      try {
        // Step 1: Preparing Resume
        updatePdfStep("1", { status: "in-progress" });
        await new Promise((resolve) => setTimeout(resolve, 800));
        updatePdfStep("1", { status: "completed" });

        // Step 2: Generating PDF with progress animation
        updatePdfStep("2", { status: "in-progress", progress: 0 });

        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setPdfSteps((prev) => {
            const step2 = prev.find((s) => s.id === "2");
            if (step2?.progress !== undefined && step2.progress < 90) {
              return prev.map((s) =>
                s.id === "2" ? { ...s, progress: Math.min((s.progress || 0) + 10, 90) } : s,
              );
            }
            return prev;
          });
        }, 100);

        const { url } = await printResume({ id });

        clearInterval(progressInterval);
        updatePdfStep("2", { status: "completed", progress: 100 });

        // Step 3: Finalizing Export
        updatePdfStep("3", { status: "in-progress" });
        await new Promise((resolve) => setTimeout(resolve, 600));

        // Force download the PDF instead of opening in new tab
        await forceDownload(url, `${title}-resume.pdf`);
        updatePdfStep("3", { status: "completed" });

        // GTM tracking for PDF download (mobile view)
        const { resume } = useResumeStore.getState();
        trackDownloadResume({
          resumeId: id,
          format: "pdf",
          templateName: resume.data.metadata.template,
          outcome: "success",
        });

        // Step 4: Download Ready
        updatePdfStep("4", { status: "completed" });
        await new Promise((resolve) => setTimeout(resolve, 800));

        setShowPdfLoadingModal(false);

        toast({
          variant: "default",
          title: t`PDF Downloaded Successfully!`,
          description: t`Your resume PDF has been downloaded to your device.`,
        });
      } catch {
        // Update current in-progress step to error state
        setPdfSteps((prev) =>
          prev.map((step) => (step.status === "in-progress" ? { ...step, status: "error" } : step)),
        );

        setTimeout(() => {
          setShowPdfLoadingModal(false);
        }, 2000);

        toast({
          variant: "error",
          title: t`Error`,
          description: t`Failed to generate PDF. Please try again.`,
        });
      }
    }, 100); // Small delay to ensure modal renders
  };

  const handleCloseModal = () => {
    setShowAuthModal(false);
  };

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      <div className="relative w-full flex-1">
        <Outlet />
      </div>

      {/* Signup Prompt Modal */}
      <SignupModal
        isOpen={showAuthModal}
        transferGuestResume
        prefilledEmail={userData.email}
        onClose={handleCloseModal}
      />

      {/* PDF Loading Modal */}
      <PdfLoadingModal isOpen={showPdfLoadingModal} steps={pdfSteps} />
    </div>
  );
};

// Mobile Edit Component
const MobileEdit = ({ leftPanelMode, activeTab, activeDocument }: { 
  leftPanelMode: "content" | "design"; 
  activeTab?: BuilderTab; 
  activeDocument?: DocumentType;
}) => (
  <div className="flex-1 overflow-hidden bg-background">
    <div className="h-full overflow-y-auto">
      {leftPanelMode === "content" ? (
        <LeftSidebar activeTab={activeTab} activeDocument={activeDocument} />
      ) : (
        <RightSidebar />
      )}
    </div>
  </div>
);

// Mobile Navigation Component (Header) - Moved outside to prevent re-creation issues
const MobileBuilderNav = ({
  resumeTitle,
  activeTab,
  setMobileTab,
  setActiveTab,
  triggerHaptic,
  sidebarOpen,
  setSidebarOpen,
}: {
  resumeTitle: string;
  activeTab: BuilderTab;
  setMobileTab: (tab: "edit" | "preview") => void;
  setActiveTab: (tab: BuilderTab) => void;
  triggerHaptic: (type: any) => void;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}) => {

  return (
    <>
      <StylionAtmosphere />
      <div className="stylion-app-shell flex-none border-b px-4 py-3">
        <div className="flex items-center justify-between rtl:flex-row-reverse">
          <div className="flex items-center gap-3 rtl:flex-row-reverse">
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="size-9 rounded-full bg-secondary/50"
                  onClick={() => triggerHaptic("light")}
                >
                  <ListIcon size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent showClose={false} side="left" className="focus-visible:outline-none rtl:left-auto rtl:right-0">
                <SheetClose asChild className="absolute left-4 top-4 z-50 rtl:left-auto rtl:right-4">
                  <Button size="icon" variant="ghost">
                    <XIcon size={24} />
                  </Button>
                </SheetClose>
                <Sidebar setOpen={setSidebarOpen} />
              </SheetContent>
            </Sheet>
            <div className="flex flex-col rtl:text-right">
              <span className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">{t`Resume Builder`}</span>
              <h1 className="max-w-[150px] truncate text-sm font-bold">
                {resumeTitle || t`Untitled Resume`}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2 rtl:flex-row-reverse">
            {/* Analysis Badge in Header for visibility */}
            {activeTab !== "analysis" && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 gap-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 rtl:flex-row-reverse"
                onClick={() => {
                  setMobileTab("edit");
                  setActiveTab("analysis");
                  triggerHaptic("light");
                }}
              >
                <ChartBar size={14} weight="fill" />
                <span className="text-xs font-bold">{t`Analysis`}</span>
              </Button>
            )}
            <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-tr from-primary to-blue-400 text-[10px] font-bold text-white shadow-lg shadow-primary/20">
              AI
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const OutletSlot = () => (
  <>
    <TabletBuilderHeader />
    <div className="relative inset-0">
      <Outlet />
    </div>
    <BuilderToolbar />
  </>
);

export const BuilderLayout = () => {
  const { isDesktop, isMobile } = useBreakpoint();
  const { triggerHaptic } = useTouchOptimization();
  const { toast } = useToast();
  const navigate = useNavigate();

  const sheet = useBuilderStore((state) => state.sheet);

  const leftSetSize = useBuilderStore((state) => state.panel.left.setSize);
  const rightSetSize = useBuilderStore((state) => state.panel.right.setSize);

  const leftHandle = useBuilderStore((state) => state.panel.left.handle);
  const rightHandle = useBuilderStore((state) => state.panel.right.handle);

  // Get the left panel mode from the builder store
  const leftPanelMode = useBuilderStore((state) => state.leftPanelMode);
  const setLeftPanelMode = useBuilderStore((state) => state.setLeftPanelMode);

  // Mobile tab state
  const [mobileTab, setMobileTab] = React.useState<"edit" | "preview">("edit");

  // Signup modal state
  const [showAuthModal, setShowAuthModal] = React.useState<boolean>(false);
  const [showPdfLoadingModal, setShowPdfLoadingModal] = useState<boolean>(false);

  // PDF Loading Steps State (same as desktop toolbar)
  const [pdfSteps, setPdfSteps] = useState<
    {
      id: string;
      title: string;
      description: string;
      status: "pending" | "in-progress" | "completed" | "error";
      progress?: number;
    }[]
  >([
    {
      id: "1",
      title: t`Preparing Resume`,
      description: t`Gathering resume data...`,
      status: "pending",
    },
    {
      id: "2",
      title: t`Generating PDF`,
      description: t`Converting to PDF format...`,
      status: "pending",
      progress: 0,
    },
    {
      id: "3",
      title: t`Finalizing Export`,
      description: t`Preparing download...`,
      status: "pending",
    },
    {
      id: "4",
      title: t`Download Ready`,
      description: t`PDF generation complete!`,
      status: "pending",
    },
  ]);

  // PDF Steps Management (same as desktop toolbar)
  const updatePdfStep = (stepId: string, updates: Partial<(typeof pdfSteps)[0]>) => {
    setPdfSteps((prev) =>
      prev.map((step) => (step.id === stepId ? { ...step, ...updates } : step)),
    );
  };

  // Resume and auth data
  const { id, title } = useResumeStore((state) => state.resume);
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const { printResume, loading } = usePrintResume();

  // Get basic user data for prefilling the signup form
  const userData = useResumeStore((state) => {
    try {
      return {
        name: state.resume.data.basics.name || "",
        email: state.resume.data.basics.email || "",
      };
    } catch {
      return {
        name: "",
        email: "",
      };
    }
  });

  // Template access check
  const templateTheme = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.theme;
    } catch {
      return {};
    }
  });
  const { canDownloadPdf } = useTemplateAccess(templateTheme);

  // Handle PDF download with guest user check (Enhanced with progress steps)
  const handlePdfExport = async () => {
    // First check if user is logged in
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }

    // Then check template access permissions
    if (!canDownloadPdf) {
      const { resume } = useResumeStore.getState();
      const templateName = resume?.data?.metadata?.template;
      trackDownloadResume({
        resumeId: id,
        format: "pdf",
        templateName,
        outcome: "redirect_to_pricing",
      });
      navigate("/pricing");
      return;
    }

    // Reset PDF steps and show loading modal with progress
    setPdfSteps([
      {
        id: "1",
        title: "Preparing Resume",
        description: "Gathering resume data...",
        status: "pending",
      },
      {
        id: "2",
        title: "Generating PDF",
        description: "Converting to PDF format...",
        status: "pending",
        progress: 0,
      },
      {
        id: "3",
        title: "Finalizing Export",
        description: "Preparing download...",
        status: "pending",
      },
      {
        id: "4",
        title: "Download Ready",
        description: "PDF generation complete!",
        status: "pending",
      },
    ]);
    setShowPdfLoadingModal(true);

    // Use setTimeout to ensure state update and re-render happens
    void setTimeout(async () => {
      try {
        // Step 1: Preparing Resume
        updatePdfStep("1", { status: "in-progress" });
        await new Promise((resolve) => setTimeout(resolve, 800));
        updatePdfStep("1", { status: "completed" });

        // Step 2: Generating PDF with progress animation
        updatePdfStep("2", { status: "in-progress", progress: 0 });

        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setPdfSteps((prev) => {
            const step2 = prev.find((s) => s.id === "2");
            if (step2?.progress !== undefined && step2.progress < 90) {
              return prev.map((s) =>
                s.id === "2" ? { ...s, progress: Math.min((s.progress || 0) + 10, 90) } : s,
              );
            }
            return prev;
          });
        }, 100);

        const { url } = await printResume({ id });

        clearInterval(progressInterval);
        updatePdfStep("2", { status: "completed", progress: 100 });

        // Step 3: Finalizing Export
        updatePdfStep("3", { status: "in-progress" });
        await new Promise((resolve) => setTimeout(resolve, 600));

        // Force download the PDF instead of opening in new tab
        await forceDownload(url, `${title}-resume.pdf`);
        updatePdfStep("3", { status: "completed" });

        // GTM tracking for PDF download (desktop view)
        const { resume } = useResumeStore.getState();
        trackDownloadResume({
          resumeId: id,
          format: "pdf",
          templateName: resume.data.metadata.template,
          outcome: "success",
        });

        // Step 4: Download Ready
        updatePdfStep("4", { status: "completed" });
        await new Promise((resolve) => setTimeout(resolve, 800));
        triggerHaptic("success");

        setShowPdfLoadingModal(false);

        toast({
          variant: "default",
          title: t`PDF Downloaded Successfully!`,
          description: t`Your resume PDF has been downloaded to your device.`,
        });
      } catch {
        // Update current in-progress step to error state
        setPdfSteps((prev) =>
          prev.map((step) => (step.status === "in-progress" ? { ...step, status: "error" } : step)),
        );

        setTimeout(() => {
          setShowPdfLoadingModal(false);
        }, 2000);

        toast({
          variant: "error",
          title: t`Error`,
          description: t`Failed to generate PDF. Please try again.`,
        });
      }
    }, 100); // Small delay to ensure modal renders
  };

  // Handle signup modal close
  const handleCloseModal = () => {
    setShowAuthModal(false);
  };

  // Helper function for save
  const onSave = () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    triggerHaptic("success");
    toast({
      variant: "default",
      title: t`Resume Saved`,
      description: t`Your resume has been saved successfully.`,
    });
  };

  // Tab navigation state from store
  const activeTab = useBuilderStore((state) => state.activeTab);
  const setActiveTab = useBuilderStore((state) => state.setActiveTab);
  
  // Document state from store
  const activeDocument = useBuilderStore((state) => state.activeDocument);
  const setActiveDocument = useBuilderStore((state) => state.setActiveDocument);
  const showCoverLetterModal = useBuilderStore((state) => state.showCoverLetterModal);
  const setShowCoverLetterModal = useBuilderStore((state) => state.setShowCoverLetterModal);
  
  // Loading state for artboard
  const isArtboardLoading = useBuilderStore((state) => state.isArtboardLoading);
  const setIsArtboardLoading = useBuilderStore((state) => state.setIsArtboardLoading);

  // Safety net: Force-hide loading overlay after 8 seconds regardless of iframe state
  // This prevents the loading overlay from being stuck forever if postMessage communication fails
  useEffect(() => {
    if (!isArtboardLoading) return;
    const safetyTimeout = setTimeout(() => {
      const stillLoading = useBuilderStore.getState().isArtboardLoading;
      if (stillLoading) {
        setIsArtboardLoading(false);
      }
    }, 8000);
    return () => clearTimeout(safetyTimeout);
  }, [isArtboardLoading, setIsArtboardLoading]);

  const documentType = useResumeStore((state) => state.resume.type);
  const resumeId = useResumeStore((state) => state.resume.id);
  const resumeTitle = useResumeStore((state) => state.resume.title);
  const isCoverLetterDocument = documentType === "coverLetter";

  // Document limit check for creating cover letters
  const { resumes } = useResumes();
  const documentCount = resumes?.length ?? 0;
  const { canCreate, maxAllowed } = useResumeLimits(documentCount);
  const [showDocumentLimitModal, setShowDocumentLimitModal] = React.useState(false);
  
  // Check if cover letter content exists for the current resume
  const hasCoverLetterContent = useResumeStore((state) => {
    try {
      const coverLetter = (state.resume.data as any).coverLetter;
      return !!(coverLetter?.body && coverLetter.body.trim().length > 0);
    } catch {
      return false;
    }
  });

  // Determine the effective document type (either from loaded doc or from tab selection)
  const effectiveDocumentType = isCoverLetterDocument ? "cover-letter" : activeDocument;

  // Handle tab change - map tabs to left panel modes
  const handleTabChange = (tab: BuilderTab) => {
    setActiveTab(tab);
    if (tab === "fill-in" || tab === "analysis") {
      setLeftPanelMode("content");
    } else if (tab === "design") {
      setLeftPanelMode("design");
    }
  };

  // Handle document type change (Resume <-> Cover Letter)
  // NOTE: We do NOT modify metadata.layout or metadata.template here anymore
  // because that corrupts the resume data when auto-saved to the database.
  // Cover letters are now saved as separate documents with their own layout.
  const handleDocumentChange = (doc: any) => {
    // If switching to cover letter and no content exists, show modal directly
    if (doc === "cover-letter" && !hasCoverLetterContent) {
      setShowCoverLetterModal(true);
      return; // Don't switch document type yet - modal will handle navigation
    }
    
    setActiveDocument(doc);
    // Reset to fill-in tab when switching documents
    setActiveTab("fill-in");
    setLeftPanelMode("content");
  };

  // Handle cover letter generation complete
  const handleCoverLetterGenerated = () => {
    // The modal navigates to the new cover letter document, so we just close
    setShowCoverLetterModal(false);
  };

  // Left panel ref for collapse/expand
  const leftPanelRef = useRef<ImperativePanelHandle>(null);
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);

  const toggleLeftPanel = React.useCallback(() => {
    const panel = leftPanelRef.current;
    if (!panel) return;
    if (panel.isCollapsed()) {
      panel.expand();
      setIsLeftCollapsed(false);
    } else {
      panel.collapse();
      setIsLeftCollapsed(true);
    }
  }, []);

  // Expose toggle function on store for BuilderTabs to use
  useEffect(() => {
    (useBuilderStore as any)._toggleLeftPanel = toggleLeftPanel;
    return () => { delete (useBuilderStore as any)._toggleLeftPanel; };
  }, [toggleLeftPanel]);

  // Desktop Layout — new 3-column design
  if (isDesktop) {
    return (
      <div className="stylion-app relative flex h-screen flex-col">
        <StylionAtmosphere />
        {/* Loading Overlay */}
        <BuilderLoadingOverlay isLoading={isArtboardLoading} />

        {/* Full-width Topbar */}
        <BuilderTopbar
          onDownload={handlePdfExport}
          onTabChange={handleTabChange}
        />

        {/* Modals */}
        <DocumentLimitModal
          isOpen={showDocumentLimitModal}
          onClose={() => setShowDocumentLimitModal(false)}
          currentCount={documentCount}
          maxAllowed={maxAllowed}
        />
        <CoverLetterGenerationModal
          isOpen={showCoverLetterModal}
          onClose={() => setShowCoverLetterModal(false)}
          onGenerated={handleCoverLetterGenerated}
        />

        {/* 3-column body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Section Rail — 190px section list */}
          <SectionRail />

          {/* Main Panel Layout with Resizable Splitter */}
          <PanelGroup direction="horizontal" className="flex-1">
            {/* Left Panel — section editor */}
            <Panel
              ref={leftPanelRef}
              defaultSize={32}
              minSize={25}
              maxSize={60}
              collapsible
              collapsedSize={0}
              onCollapse={() => setIsLeftCollapsed(true)}
              onExpand={() => setIsLeftCollapsed(false)}
              className="stylion-app-muted-panel z-20 flex flex-col overflow-hidden shadow-sm"
            >
              <div className="flex-1 h-full overflow-hidden">
                {activeTab === "design" ? (
                  <RightSidebar />
                ) : (
                  <LeftSidebar activeTab={activeTab} activeDocument={effectiveDocumentType} />
                )}
              </div>
            </Panel>

            {/* Resize Handle */}
            <PanelResizeHandle className="group relative w-2 cursor-col-resize bg-border/40 transition-all hover:bg-primary/20">
              <div className="absolute inset-y-0 left-[-5px] rtl:right-[-5px] rtl:left-auto flex w-[14px] cursor-col-resize items-center justify-center transition-opacity">
                <div className="flex h-8 w-[6px] flex-col items-center justify-center gap-[3px] rounded-sm border border-gray-300 bg-gray-200 opacity-60 transition-all group-hover:border-primary/50 group-hover:bg-primary/40 group-hover:opacity-100 dark:border-gray-500 dark:bg-gray-600 dark:group-hover:border-primary/50 dark:group-hover:bg-primary/50">
                  <div className="h-[2px] w-full rounded-full bg-gray-400 dark:bg-gray-400" />
                  <div className="h-[2px] w-full rounded-full bg-gray-400 dark:bg-gray-400" />
                  <div className="h-[2px] w-full rounded-full bg-gray-400 dark:bg-gray-400" />
                </div>
              </div>
            </PanelResizeHandle>

            {/* Right Panel — Preview */}
            <Panel
              defaultSize={68}
              minSize={40}
              className="relative flex flex-col overflow-hidden"
            >
              {/* Document Preview */}
              <div className="stylion-preview-canvas relative flex flex-1 items-start justify-center overflow-auto p-0 pt-0">

                {/* Floating Design Tab */}
                <div className="absolute right-5 rtl:left-5 rtl:right-auto top-1/2 -translate-y-1/2 z-50">
                  <button
                    onClick={() => handleTabChange("design")}
                    className={cn(
                      "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold transition-all duration-200 shadow-sm hover:shadow-md",
                      leftPanelMode === "design"
                        ? "bg-[#e8885e]/20 text-[#cd7858]"
                        : "bg-[#fdf5ef] text-[#cd7858] hover:bg-[#e8885e]/20",
                    )}
                  >
                    <PaintBrushHousehold size={14} weight="duotone" />
                    <span>{t`Design`}</span>
                  </button>
                </div>

                <div className="w-full h-full relative z-10 flex flex-col pt-0">
                  <Outlet />
                </div>
              </div>

              {/* Toolbar — floating top center */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50">
                <BuilderToolbar />
              </div>
            </Panel>
          </PanelGroup>
        </div>

        {/* Modals */}
        <SignupModal
          isOpen={showAuthModal}
          transferGuestResume
          prefilledEmail={userData.email}
          onClose={handleCloseModal}
        />
        <PdfLoadingModal isOpen={showPdfLoadingModal} steps={pdfSteps} />
      </div>
    );
  }

  // Mobile Layout - Option 6 (Anchored Nav)
  if (isMobile) {
    return (
      <>
        <MobileLayout 
          isArtboardLoading={isArtboardLoading} 
          onDownload={handlePdfExport} 
        />
        <SignupModal
          isOpen={showAuthModal}
          transferGuestResume
          prefilledEmail={userData.email || ""}
          onClose={handleCloseModal}
        />
        <PdfLoadingModal isOpen={showPdfLoadingModal} steps={pdfSteps} />
      </>
    );
  }

  // Tablet Layout - Fallback to original sheet-based approach for tablets
  return (
    <div className="relative">
      {/* Loading Overlay */}
      <BuilderLoadingOverlay isLoading={isArtboardLoading} />
      
      <Sheet open={sheet.left.open} onOpenChange={sheet.left.setOpen}>
        <VisuallyHidden>
          <SheetHeader>
            <SheetTitle />
            <SheetDescription />
          </SheetHeader>
        </VisuallyHidden>

        <SheetContent
          side="left"
          showClose={false}
          className="sheet-content-left top-16 p-0 sm:max-w-xl"
          onOpenAutoFocus={onOpenAutoFocus}
        >
          <LeftSidebar />
        </SheetContent>
      </Sheet>

      <OutletSlot />

      <Sheet open={sheet.right.open} onOpenChange={sheet.right.setOpen}>
        <SheetContent
          side="right"
          showClose={false}
          className="top-16 p-0 sm:max-w-xl"
          onOpenAutoFocus={onOpenAutoFocus}
        >
          <VisuallyHidden>
            <SheetHeader>
              <SheetTitle />
              <SheetDescription />
            </SheetHeader>
          </VisuallyHidden>

          <RightSidebar />
        </SheetContent>
      </Sheet>

      {/* Signup Prompt Modal */}
      <SignupModal
        isOpen={showAuthModal}
        transferGuestResume
        prefilledEmail={userData.email}
        onClose={handleCloseModal}
      />

      {/* PDF Loading Modal */}
      <PdfLoadingModal isOpen={showPdfLoadingModal} steps={pdfSteps} />
    </div>
  );
};
